/**
 * «Доставки»: что везём клиентам и что забираем — по дням наперёд, с правкой на месте.
 * Числа — те же, что у `prokat-runner delivery-report`. Читает только свою базу (поля
 * доставки сделок Bitrix раскладывает фон `delivery_sync`); правка пишет в боевой Bitrix
 * и в договор CRM. Спека `docs/specs/2026-10-08-delivery-design.md`.
 */
import { useGetIdentity } from "@refinedev/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  ExternalLink,
  History,
  MessageCircle,
  Pencil,
  Plus,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker, type Окно } from "@/components/DateRangePicker";
import { DealSide, type DealSideTab } from "@/components/DealSide";
import {
  EditForm,
  type Form,
  formOfNow,
  ResultLine,
  TIME_SALON,
  TITLE,
} from "@/components/DeliveryEditForm";
import { DeliveryNeed } from "@/components/DeliveryNeed";
import { CLIENT_CHATS, has } from "@/lib/access";
import {
  addDays,
  type DeliveryDay,
  type DeliveryEdit,
  type DeliveryReport,
  type DeliveryResult,
  type DeliveryRow,
  type DeliveryStatus,
  type Direction,
  dm,
  iso,
  loadDelivery,
  loadDeliveryDeal,
  phone,
  saveDelivery,
  timeOf,
  weekday,
} from "@/lib/delivery";
import type { CurrentUser } from "@/lib/providers";
import { channelOf } from "@/pages/ChatsPage";
import { DeliveryOrders, ordersPeriod } from "@/pages/DeliveryOrders";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const input =
  "h-8 w-full rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] text-fg placeholder:text-fg-faint";
const btn =
  "inline-flex h-7 items-center gap-1 rounded-[var(--radius-ui)] border border-border px-2 text-[12px] text-fg-muted hover:bg-bg-hover disabled:opacity-50";

const STAGE: Record<string, string> = { "4": "бронь", "2": "выдано" };
const STATUS_TONE: Record<string, string> = {
  need: "bg-err-bg text-err",
  go: "bg-warn-bg text-warn",
  done: "bg-ok-bg text-ok",
  cancel: "bg-neutral-bg text-fg-muted",
  "": "bg-neutral-bg text-fg-muted",
};

/** Окна периода: доставки смотрят ВПЕРЁД — «7 дней» здесь сегодня и шесть следующих. */
function окна(): Окно[] {
  const now = new Date();
  const t = iso(now);
  const forward = (n: number, hint: string): Окно => ({
    label: `${n} дней`,
    hint,
    from: t,
    to: addDays(t, n - 1),
  });
  return [
    { label: "сегодня", hint: "только сегодня", from: t, to: t },
    {
      label: "завтра",
      hint: "только завтра",
      from: addDays(t, 1),
      to: addDays(t, 1),
    },
    {
      label: "вчера",
      hint: "только вчера",
      from: addDays(t, -1),
      to: addDays(t, -1),
    },
    forward(7, "сегодня и 6 дней вперёд"),
    forward(14, "сегодня и 13 дней вперёд — как открывается экран"),
    forward(30, "сегодня и 29 дней вперёд"),
    {
      label: "этот месяц",
      hint: "с первого по последнее число",
      from: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    },
  ];
}

const formOfRow = (r: DeliveryRow): Form => ({
  need: r.kind === "active",
  status: r.status,
  address: r.address,
  time: r.time ?? "",
  time_salon: r.time_salon ?? "",
  courier: r.courier,
  courier_phone: r.courier_phone,
  comment: r.comment,
});

function Log({ deal, direction }: { deal: number; direction: Direction }) {
  const q = useQuery({
    queryKey: ["delivery-deal", deal],
    queryFn: () => loadDeliveryDeal(deal),
  });
  if (q.isPending)
    return <div className="mt-2 text-[12px] text-fg-muted">…</div>;
  if (q.isError)
    return (
      <div className="mt-2 text-[12px] text-err">
        Журнал не открылся: {q.error.message}
      </div>
    );
  const rows = q.data.changes.filter((c) => c.direction === direction);
  if (!rows.length)
    return (
      <div className="mt-2 text-[12px] text-fg-faint">
        Правок с начала наблюдения (08.10.26) не было.
      </div>
    );
  return (
    <ul
      className="mt-2 grid gap-0.5 text-[12px]"
      data-annot={`журнал доставки, сделка ${deal}`}
    >
      {rows.map((c, i) => (
        <li key={i} className="flex flex-wrap gap-x-2">
          <span className="tabular-nums text-fg-faint">
            {dm(c.at)} {c.at.slice(11, 16)}
          </span>
          <span
            className={
              c.source === "robot" ? "font-medium text-err" : "text-fg-muted"
            }
          >
            {c.who || c.source_label}
            {c.who && c.source !== "panel" ? ` (${c.source_label})` : ""}
          </span>
          <span className="text-fg">
            {c.field}: {c.old || "пусто"} → {c.new || "пусто"}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** «14:00» + сдвиг салона к Москве → «18:00» — подпись местного времени у строки не из Москвы. */
function plusHours(hhmm: string, tz: number): string {
  const h = (((Number(hhmm.slice(0, 2)) + tz) % 24) + 24) % 24;
  return `${String(h).padStart(2, "0")}${hhmm.slice(2)}`;
}

function Row({
  r,
  statuses,
  canEdit,
  canChat,
  onChanged,
  onOpen,
}: {
  r: DeliveryRow;
  statuses: DeliveryStatus[];
  canEdit: boolean;
  canChat: boolean;
  onChanged: () => void;
  /** Открыть панель справа: сделка и переписка клиента. */
  onOpen: (r: DeliveryRow, tab: DealSideTab) => void;
}) {
  const [editing, setEditing] = useState<Form | null>(null);
  const [log, setLog] = useState(false);
  const [result, setResult] = useState<DeliveryResult | null>(null);
  const [failure, setFailure] = useState("");
  const done = (x: DeliveryResult) => {
    setResult(x);
    setEditing(null);
    setFailure("");
    onChanged();
  };
  const quick = useMutation({
    mutationFn: (body: DeliveryEdit) =>
      saveDelivery(r.deal_id, r.direction, body),
    onSuccess: done,
    onError: (e: Error) => setFailure(e.message),
  });
  const time = timeOf(r.time, r.day);
  const windowText = r.window_start && time ? `${r.window_start.slice(11, 16)}–${time}` : time;
  const localText = r.tz && time ? ` (местное ${plusHours(time, r.tz)})` : "";
  const salon = timeOf(r.time_salon, r.day);
  const dim = r.kind === "cancelled" || r.kind === "contract";
  const mark = "rounded px-1.5 py-0.5 text-[11.5px] font-medium";
  let note: ReactNode = null;
  if (r.kind === "guard")
    note = (
      <span
        className={`${mark} inline-flex items-center gap-1 bg-err-bg text-err`}
      >
        <Bot size={12} /> отменил робот
        {r.guard_at ? ` ${dm(r.guard_at)} ${r.guard_at.slice(11, 16)}` : ""}
        {r.guard_status_label ? ` — стояло «${r.guard_status_label}»` : ""}
      </span>
    );
  else if (r.kind === "cancelled")
    note = (
      <span className={`${mark} bg-neutral-bg text-fg-muted`}>
        отменена, адрес остался
      </span>
    );
  else if (r.kind === "contract")
    note = (
      <span className={`${mark} bg-warn-bg text-warn`}>
        отмечена в договоре, в Bitrix не заведена
      </span>
    );
  else if (r.contract.id && r.contract.need !== 1)
    note = (
      <span className={`${mark} bg-warn-bg text-warn`}>
        в договоре не отмечено — робот может отменить
      </span>
    );
  return (
    <li
      className={`border-b border-border px-4 py-3 last:border-b-0 ${dim ? "opacity-75" : ""}`}
      data-annot={`${TITLE[r.direction].toLowerCase()}: сделка ${r.deal_id}`}
    >
      <div className="flex items-start gap-3 text-[13px]">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          <span className="tabular-nums text-fg-muted">
            {weekday(r.day)} {dm(r.day)}
          </span>
          {r.kind === "active" && !time ? (
            <span className="font-medium text-err">нет времени</span>
          ) : (
            <span className="font-semibold tabular-nums">{windowText || "—"}{localText}</span>
          )}
          {r.city && <span className="text-fg-muted">{r.city}</span>}
          {r.status && (
            <span
              className={`${mark} ${STATUS_TONE[r.status_kind] ?? STATUS_TONE[""]}`}
            >
              {r.status_label}
            </span>
          )}
          {r.client_at && (
            <span className={`${mark} bg-ok-bg text-ok`} title="Заказал клиент со страницы брони">
              клиент · {dm(r.client_at)} {r.client_at.slice(11, 16)}
            </span>
          )}
          <Link
            to={`/deals/${r.deal_id}`}
            className="text-accent hover:underline"
            title="Сделка — в панели справа; Ctrl+клик — карточка в новой вкладке"
            onClick={(e) => {
              if (e.ctrlKey || e.metaKey || e.shiftKey) return;
              e.preventDefault();
              onOpen(r, "deal");
            }}
          >
            сделка {r.deal_id}
          </Link>
          {canChat && r.chat && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-accent hover:underline"
              title="Переписка с клиентом — в панели справа"
              onClick={() => onOpen(r, "chat")}
            >
              <MessageCircle size={12} /> чат ·{" "}
              {channelOf(r.chat.channel).short}
            </button>
          )}
          <a
            href={`https://unirenter.bitrix24.ru/crm/deal/details/${r.deal_id}/`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 text-[12px] text-fg-faint hover:text-fg"
            title="Открыть в Bitrix"
          >
            Bitrix <ExternalLink size={11} />
          </a>
          <span className="text-[12px] text-fg-faint">
            {STAGE[r.stage] ?? r.stage}
          </span>
        </div>
        <span className="flex flex-none items-center gap-1.5">
          {canEdit && (
            <DeliveryNeed
              deal={r.deal_id}
              direction={r.direction}
              on={r.kind === "active"}
              guard={r.kind === "guard"}
              onSaved={done}
            />
          )}
          <button
            type="button"
            className={btn}
            onClick={() => setLog((v) => !v)}
            title="Кто и что менял"
          >
            <History size={12} /> журнал
          </button>
          {canEdit && (
            <button
              type="button"
              className={btn}
              onClick={() => setEditing(editing ? null : formOfRow(r))}
            >
              <Pencil size={12} /> править
            </button>
          )}
        </span>
      </div>
      <div className="mt-1 text-[13px]">
        {r.address ? (
          <span className="text-fg">{r.address}</span>
        ) : r.kind === "active" ? (
          <span className="font-medium text-err">нет адреса</span>
        ) : (
          <span className="text-fg-faint">адреса нет</span>
        )}
        {!r.address && r.contract.address && (
          <span className="ml-2 text-[12px] text-fg-muted">
            в договоре: {r.contract.address}
            {canEdit && (
              <button
                type="button"
                className="ml-1.5 text-accent hover:underline"
                onClick={() =>
                  setEditing({
                    ...formOfRow(r),
                    need: true,
                    address: r.contract.address,
                  })
                }
              >
                взять
              </button>
            )}
          </span>
        )}
        {(r.phone || r.note) && (
          <div className="text-fg-muted">
            {r.phone ? `тел. курьеру +${r.phone}` : ""}
            {r.phone && r.note ? " · " : ""}
            {r.note}
          </div>
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[12px] text-fg-muted">
        <span>
          {r.client.name || "клиент без имени"}
          {r.client.phones.slice(0, 2).map((p) => (
            <a
              key={p}
              href={`tel:+${p}`}
              className="ml-1.5 tabular-nums text-fg hover:underline"
            >
              {phone(p)}
            </a>
          ))}
        </span>
        {(r.courier || r.courier_phone) && (
          <span>
            курьер: {r.courier} {r.courier_phone}
          </span>
        )}
        {salon && (
          <span>
            {TIME_SALON[r.direction].toLowerCase()}: {salon}
          </span>
        )}
        {r.comment && <span>курьеру: {r.comment}</span>}
      </div>
      {note && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {note}
          {canEdit && r.kind === "active" && (
            <button
              type="button"
              className={btn}
              disabled={quick.isPending}
              onClick={() => quick.mutate({ need: true })}
            >
              Отметить в договоре
            </button>
          )}
        </div>
      )}
      {failure && (
        <div className="mt-1 text-[12px] text-err">Не записано: {failure}</div>
      )}
      {result && (
        <div className="mt-1">
          <ResultLine r={result} />
        </div>
      )}
      {editing && (
        <EditForm
          deal={r.deal_id}
          direction={r.direction}
          initial={editing}
          statuses={statuses}
          onSaved={done}
          onCancel={() => setEditing(null)}
        />
      )}
      {log && <Log deal={r.deal_id} direction={r.direction} />}
    </li>
  );
}

function DayStrip({
  days,
  selected,
  onPick,
}: {
  days: DeliveryDay[];
  selected: string;
  onPick: (day: string) => void;
}) {
  const today = iso(new Date());
  return (
    <div
      className="grid grid-flow-col auto-cols-[minmax(74px,1fr)] gap-1.5 overflow-x-auto pb-1"
      data-annot="полоса дней"
    >
      {days.map((d) => {
        const empty = !d.give && !d.take;
        const on = selected === d.day;
        return (
          <button
            key={d.day}
            type="button"
            onClick={() => onPick(on ? "" : d.day)}
            title={on ? "Показать все дни" : "Показать только этот день"}
            className={
              `flex flex-col gap-0.5 rounded-[var(--radius-card)] border px-2 py-1.5 text-left ` +
              (on
                ? "border-accent bg-accent-dim"
                : "border-border bg-bg-subtle hover:bg-bg-hover") +
              (empty ? " opacity-60" : "")
            }
          >
            <span className="flex items-center gap-1 text-[11.5px] text-fg-muted">
              <span className={d.day === today ? "font-semibold text-fg" : ""}>
                {weekday(d.day)} {dm(d.day)}
              </span>
              {d.gaps > 0 && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-err"
                  title={`без адреса или времени: ${d.gaps}`}
                />
              )}
              {d.guards > 0 && (
                <Bot
                  size={11}
                  className="text-err"
                  aria-label={`отменил робот: ${d.guards}`}
                />
              )}
            </span>
            <span className="text-[13px] tabular-nums">
              <span
                className={d.give ? "font-semibold text-fg" : "text-fg-faint"}
              >
                ↑ {d.give}
              </span>
              <span
                className={`ml-2 ${d.take ? "font-semibold text-fg" : "text-fg-faint"}`}
              >
                ↓ {d.take}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function AddBlock({
  r,
  onChanged,
}: {
  r: DeliveryReport;
  onChanged: () => void;
}) {
  const [deal, setDeal] = useState("");
  const [direction, setDirection] = useState<Direction>("start");
  const [open, setOpen] = useState<number | null>(null);
  const [result, setResult] = useState<DeliveryResult | null>(null);
  const q = useQuery({
    queryKey: ["delivery-deal", open],
    queryFn: () => loadDeliveryDeal(open!),
    enabled: open !== null,
  });
  const cancel =
    r.statuses[direction].find((s) => s.kind === "cancel")?.code ?? "";
  return (
    <section className={`${card} p-4`} data-annot="добавить доставку">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setResult(null);
          if (/^\d{4,9}$/.test(deal.trim())) setOpen(Number(deal.trim()));
        }}
      >
        <span className="text-[13px] font-medium">
          Добавить по номеру сделки
        </span>
        <input
          className={`${input} !w-[130px]`}
          inputMode="numeric"
          value={deal}
          placeholder="1252456"
          onChange={(e) => {
            setDeal(e.target.value);
            setOpen(null);
          }}
          aria-label="Номер сделки"
        />
        <select
          className={`${input} !w-auto`}
          value={direction}
          onChange={(e) => setDirection(e.target.value as Direction)}
          aria-label="Направление"
        >
          <option value="start">доставить клиенту</option>
          <option value="end">забрать у клиента</option>
        </select>
        <button type="submit" className={btn}>
          <Plus size={12} /> Открыть
        </button>
      </form>
      {open !== null && q.isPending && (
        <div className="mt-2 text-[12px] text-fg-muted">…</div>
      )}
      {open !== null && q.isError && (
        <div className="mt-2 text-[12px] text-err">{q.error.message}</div>
      )}
      {open !== null && q.isSuccess && (
        <EditForm
          key={`${open}-${direction}`}
          deal={open}
          direction={direction}
          initial={formOfNow(q.data.now[direction], cancel)}
          statuses={r.statuses[direction]}
          onSaved={(x) => {
            setResult(x);
            setOpen(null);
            setDeal("");
            onChanged();
          }}
          onCancel={() => setOpen(null)}
        />
      )}
      {result && (
        <div className="mt-2">
          <ResultLine r={result} />
        </div>
      )}
    </section>
  );
}

function List({
  direction,
  rows,
  r,
  canChat,
  onChanged,
  onOpen,
}: {
  direction: Direction;
  rows: DeliveryRow[];
  r: DeliveryReport;
  canChat: boolean;
  onChanged: () => void;
  onOpen: (r: DeliveryRow, tab: DealSideTab) => void;
}) {
  return (
    <section className={card} data-annot={`список «${TITLE[direction]}»`}>
      <div className="flex items-baseline gap-2 border-b border-border px-4 py-3">
        <h2 className="text-[14px] font-semibold">
          {direction === "start" ? "↑" : "↓"} {TITLE[direction]}
        </h2>
        <span className="text-[12.5px] tabular-nums text-fg-muted">
          {rows.length}
        </span>
      </div>
      {rows.length ? (
        <ul>
          {rows.map((x) => (
            <Row
              key={`${x.deal_id}-${x.direction}`}
              r={x}
              statuses={r.statuses[direction]}
              canEdit={r.can_edit}
              canChat={canChat}
              onChanged={onChanged}
              onOpen={onOpen}
            />
          ))}
        </ul>
      ) : (
        <div className="px-4 py-6 text-[12.5px] text-fg-faint">Нет.</div>
      )}
    </section>
  );
}

export function DeliveryPage() {
  const [sp, setSp] = useSearchParams();
  const qc = useQueryClient();
  const today = iso(new Date());
  const from = sp.get("from") || today;
  const to = sp.get("to") || addDays(from, 13);
  const city = sp.get("city") || "";
  const day = sp.get("day") || "";
  const gaps = sp.get("gaps") === "1";
  // Вкладка «Заявки» — заявки на курьера из prokat; период у неё свой, назад от сегодня.
  const tab = sp.get("tab") === "orders" ? "orders" : "list";
  const op = ordersPeriod(sp.get("ofrom"), sp.get("oto"));
  // Панель справа — сделка и чат клиента; в адресе, чтобы ссылкой открывалась та же.
  const openDeal = Number(sp.get("deal")) || null;
  const { data: user } = useGetIdentity<CurrentUser>();
  const canChat = has(user, CLIENT_CHATS);
  const put = (patch: Record<string, string>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch))
      v ? next.set(k, v) : next.delete(k);
    setSp(next, { replace: true });
  };
  const q = useQuery({
    queryKey: ["delivery", from, to, city],
    queryFn: () => loadDelivery(from, to, city),
    refetchInterval: 5 * 60_000,
    enabled: tab === "list",
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["delivery"] });
    void qc.invalidateQueries({ queryKey: ["delivery-deal"] });
  };
  const r = q.data;
  const open = (x: DeliveryRow, tab: DealSideTab) =>
    put({
      deal: String(x.deal_id),
      chat: x.chat ? String(x.chat.id) : "",
      side: tab === "chat" ? "chat" : "",
    });
  // Доставки открытой сделки: шапка панели и «что везём»; сделки вне периода в списках нет.
  const opened = r
    ? [...r.give, ...r.take].filter((x) => x.deal_id === openDeal)
    : [];
  const pick = (rows: DeliveryRow[]) =>
    rows.filter(
      (x) =>
        (!day || x.day === day) &&
        (!gaps || x.gaps.length > 0 || x.kind === "guard"),
    );
  const chip = (on: boolean) =>
    `h-8 rounded-[var(--radius-ui)] border px-2.5 text-[12.5px] ` +
    (on
      ? "border-accent bg-accent-dim text-fg"
      : "border-border text-fg-muted hover:bg-bg-hover");
  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-2 text-[18px] font-semibold tracking-[-0.01em]">
          Доставки
        </h1>
        <div className="mr-2 flex gap-1" role="radiogroup" aria-label="Вид">
          {(
            [
              ["list", "Доставки по дням"],
              ["orders", "Заявки курьерам"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={tab === id}
              className={chip(tab === id)}
              onClick={() => put({ tab: id === "list" ? "" : id })}
            >
              {label}
            </button>
          ))}
        </div>
        {tab === "list" && (
          <>
            <DateRangePicker
              label="период"
              from={from}
              to={to}
              windows={окна()}
              onChange={(a, b) => put({ from: a, to: b, day: "" })}
            />
            <button
              type="button"
              className={chip(!city)}
              onClick={() => put({ city: "" })}
            >
              Все города
            </button>
            {(r?.cities ?? []).map((c) => (
              <button
                key={c}
                type="button"
                className={chip(city === c)}
                onClick={() => put({ city: city === c ? "" : c })}
              >
                {c}
              </button>
            ))}
            <button
              type="button"
              className={chip(gaps)}
              onClick={() => put({ gaps: gaps ? "" : "1" })}
              title="Нет адреса или времени, либо доставку отменил робот"
            >
              Только с пробелами
            </button>
            {r && (
              <span
                className="ml-auto text-[12px] text-fg-faint"
                title="Когда фон последний раз сверял доставки с копией Bitrix"
              >
                сверено{" "}
                {r.synced
                  ? `${dm(r.synced)} ${r.synced.slice(11, 16)}`
                  : "— ещё ни разу"}
              </span>
            )}
          </>
        )}
      </div>
      {tab === "orders" && (
        <DeliveryOrders
          from={op.from}
          to={op.to}
          onPeriod={(a, b) => put({ ofrom: a, oto: b })}
          onOpen={(x) =>
            put({
              deal: String(x.deal_id),
              chat: x.chat ? String(x.chat.id) : "",
              side: "",
            })
          }
        />
      )}
      {tab === "list" && q.isPending && (
        <div className="text-[12.5px] text-fg-muted">…</div>
      )}
      {tab === "list" && q.isError && (
        <div className="text-[12.5px] text-err">
          Не удалось получить доставки: {q.error.message}
        </div>
      )}
      {tab === "list" && r && (
        <>
          <DayStrip
            days={r.days}
            selected={day}
            onPick={(d) => put({ day: d })}
          />
          <div className="text-[12px] text-fg-faint">
            ↑ доставить · ↓ забрать · красная точка — нет адреса или времени ·
            робот — доставку отменила CRM, а не человек
            {day ? ` · показан только ${weekday(day)} ${dm(day)}` : ""}
          </div>
          <div className="grid items-start gap-4 xl:grid-cols-2">
            <List
              direction="start"
              rows={pick(r.give)}
              r={r}
              canChat={canChat}
              onChanged={refresh}
              onOpen={open}
            />
            <List
              direction="end"
              rows={pick(r.take)}
              r={r}
              canChat={canChat}
              onChanged={refresh}
              onOpen={open}
            />
          </div>
          {r.can_edit ? (
            <AddBlock r={r} onChanged={refresh} />
          ) : (
            <div className="text-[12px] text-fg-faint">
              Правка доставок — по праву «Править доставки».
            </div>
          )}
        </>
      )}
      {openDeal && (
        <DealSide
          dealId={openDeal}
          chatId={Number(sp.get("chat")) || opened[0]?.chat?.id || null}
          tab={sp.get("side") === "chat" ? "chat" : "deal"}
          title={
            <>
              Сделка {openDeal}
              {opened[0]?.client.name && (
                <span className="font-normal text-fg-muted">
                  {" "}
                  · {opened[0].client.name}
                </span>
              )}
            </>
          }
          onTab={(tab) => put({ side: tab === "chat" ? "chat" : "" })}
          onChat={(id) => put({ chat: String(id) })}
          onClose={() => put({ deal: "", chat: "", side: "" })}
        >
          {opened.length > 0 && (
            <ul
              className="grid gap-1 text-[12.5px]"
              data-annot={`доставки сделки ${openDeal}`}
            >
              {opened.map((x) => (
                <li key={x.direction} className="flex flex-wrap gap-x-2">
                  <span className="font-medium">
                    {x.direction === "start" ? "↑ доставить" : "↓ забрать"}
                  </span>
                  <span className="tabular-nums">
                    {weekday(x.day)} {dm(x.day)} {timeOf(x.time, x.day)}
                  </span>
                  <span
                    className={
                      x.address ? "text-fg-muted" : "font-medium text-err"
                    }
                  >
                    {x.address || "нет адреса"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </DealSide>
      )}
    </div>
  );
}
